import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Plus, Pencil, Loader2, AlertCircle, RefreshCw, Bell, Anchor, RotateCcw, Ban, Trash2, X, Search, AlertTriangle, Users,
} from 'lucide-react';
import { PageHeader, Button, Avatar } from '../ui-v2';
import { Modal, Input, Select, Badge, EmptyState, Tooltip, ToggleGroup, ToggleItem } from '../ui-v2.5';
import {
  listReglasAviso, getReglaAviso, crearReglaAviso, actualizarReglaAviso, desactivarReglaAviso,
  eliminarReglaAvisoPermanente, resolverDestinatarios, diagnosticoReglas,
  listNegocios, listPuestos, listAreas,
} from './api';
import EliminarModal from './components/EliminarModal';
import PersonaPickerMiembro from './components/PersonaPickerMiembro';
import IncorporacionesTabs from './IncorporacionesTabs';
import { activosDelAmbito, INTERNO } from './pertenencia';

/**
 * Avisos (reglas de aviso) — última pantalla de configuración (§4.4 de Fase 6).
 *
 * - Secciones POR NEGOCIO (todos los negocios, incluso sin reglas): la regla BASE arriba, destacada
 *   (icono ancla + badge «Regla base» + acento); las específicas subordinadas debajo. Banner rojo en
 *   los negocios sin regla base activa (del /diagnostico), donde falta.
 * - Panel fijo de COMPROBACIÓN: "¿quién se entera si entra un {puesto} en {negocio}?" → /resolver.
 * - Editor con EFECTO COMBINADO (base + la regla que se toca), distinguiendo la procedencia.
 * - Cada destinatario aparece UNA vez con TODAS sus procedencias (motivos[]): de qué regla, base o
 *   puesto, y si por área o nombrado. Áreas sin miembros señaladas donde aparezcan.
 */

const ALCANCE_LABEL = { base: 'Base', puesto: 'Puesto' };
const cuerpoError = (e) => (e && e.response && e.response.data) || {};

// ─────────────────────────── Lista de destinatarios resueltos (compartida: panel + editor) ───────────────────────────
function DestinatariosResueltos({ data, cargando, error, onReintentar }) {
  if (cargando) {
    return <div className="flex items-center gap-2 py-6 text-sm text-v25-ink-500"><Loader2 className="h-4 w-4 animate-spin" /> Calculando destinatarios…</div>;
  }
  if (error) {
    return (
      <div className="py-4 text-sm text-red-600">
        No se pudo calcular. {onReintentar && <button type="button" onClick={onReintentar} className="underline">Reintentar</button>}
      </div>
    );
  }
  if (!data) return <p className="py-4 text-xs text-v25-ink-400">Elige un negocio (y opcionalmente un puesto) para ver quién se entera.</p>;

  const { destinatarios = [], avisos = {}, reglas_aplicadas = [] } = data;
  const sinMiembros = avisos.areas_sin_miembros || [];
  return (
    <div className="space-y-3">
      {reglas_aplicadas.length === 0 ? (
        <div className="flex items-start gap-2 rounded-v25-md border border-v25-expired-bd bg-v25-expired-bg p-3 text-xs text-v25-expired-fg">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          No hay ninguna regla activa para esta combinación: <strong>ninguna alta avisaría a nadie</strong>.
        </div>
      ) : destinatarios.length === 0 ? (
        <div className="flex items-start gap-2 rounded-v25-md border border-v25-expired-bd bg-v25-expired-bg p-3 text-xs text-v25-expired-fg">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          Las reglas aplicables (<strong>{reglas_aplicadas.length}</strong>) no avisan a nadie: áreas vacías y sin personas directas.
        </div>
      ) : (
        <>
          <p className="text-xs text-v25-ink-500">
            <strong>{destinatarios.length}</strong> destinatario(s) · {reglas_aplicadas.length} regla(s) aplicada(s)
          </p>
          <ul className="divide-y divide-v25-line rounded-v25-md border border-v25-line">
            {destinatarios.map((d) => (
              <li key={d.id} className="flex items-start gap-2 px-3 py-2">
                <Avatar name={d.nombre} size="sm" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm text-v25-ink-800">{d.nombre}</div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {d.motivos.map((m, i) => (
                      <span key={i} className="inline-flex items-center gap-1 rounded-v25-full bg-v25-surface-sunken px-2 py-0.5 text-[10px] text-v25-ink-500">
                        <span className={m.alcance === 'base' ? 'font-semibold text-v25-ink-700' : ''}>{ALCANCE_LABEL[m.alcance] || m.alcance}</span>
                        · {m.via === 'area' ? `área ${m.area ? m.area.nombre : ''}` : 'nombrado'}
                      </span>
                    ))}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {sinMiembros.length > 0 && (
        <div className="flex items-start gap-2 rounded-v25-md border border-v25-received-bd bg-v25-received-bg p-3 text-xs text-v25-received-fg">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Áreas implicadas <strong>sin miembros</strong> (no avisan a nadie): {sinMiembros.map((a) => a.area.nombre).join(', ')}.</span>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────── Panel fijo de comprobación ───────────────────────────
function PanelComprobacion({ negocios, puestos }) {
  const [negocioId, setNegocioId] = useState('');
  const [puestoId, setPuestoId] = useState('');
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(false);

  const puestosDelNegocio = useMemo(
    /* Solo puestos de INTERNO: un aviso cuelga de un negocio (`incorp_reglas_aviso.negocio_id`) y
     * un puesto de colaborador no tiene negocio. Antes esto salia bien por accidente -su
     * `negocio_id` a NULL no casaba con ningun id-; ahora se dice, porque una regla que se cumple
     * sola deja de cumplirse el dia que alguien toca lo de al lado. */
    () => activosDelAmbito(puestos, { tipo: INTERNO, negocioId }),
    [puestos, negocioId],
  );

  const resolver = useCallback(async (nid, pid) => {
    if (!nid) { setData(null); return; }
    setCargando(true); setError(false);
    try { setData(await resolverDestinatarios(Number(nid), pid ? Number(pid) : null)); }
    catch (e) { console.error('[incorp][avisos] resolver', e); setError(true); }
    finally { setCargando(false); }
  }, []);

  function cambiarNegocio(v) { setNegocioId(v); setPuestoId(''); resolver(v, ''); }
  function cambiarPuesto(v) { setPuestoId(v); resolver(negocioId, v); }

  return (
    <div className="rounded-v25-lg border border-v25-line bg-white p-4">
      <div className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-v25-ink-800">
        <Search className="h-4 w-4" /> ¿Quién se entera de un alta?
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Select label="Negocio" value={negocioId} onChange={(e) => cambiarNegocio(e.target.value)}>
          <option value="">Selecciona un negocio…</option>
          {negocios.map((n) => <option key={n.id} value={n.id}>{n.nombre}</option>)}
        </Select>
        <Select label="Puesto (opcional)" value={puestoId} onChange={(e) => cambiarPuesto(e.target.value)} disabled={!negocioId}
          hint={negocioId && puestosDelNegocio.length === 0 ? 'Sin puestos activos: solo se aplica la base.' : 'Sin puesto = solo la regla base.'}>
          <option value="">Solo la base del negocio</option>
          {puestosDelNegocio.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
        </Select>
      </div>
      <div className="mt-3">
        <DestinatariosResueltos data={data} cargando={cargando} error={error} onReintentar={() => resolver(negocioId, puestoId)} />
      </div>
    </div>
  );
}

// ─────────────────────────── Fila de regla dentro de la sección ───────────────────────────
function ReglaRow({ regla, accion, onEditar, onDesactivar, onReactivar, onEliminar }) {
  return (
    <div className={`flex items-center gap-3 rounded-v25-md border p-3 ${regla.es_base ? 'border-v25-line-strong bg-v25-surface-sunken' : 'border-v25-line bg-white'} ${regla.activa ? '' : 'opacity-70'}`}
      style={regla.es_base ? { borderLeft: '4px solid #0066b3' } : { marginLeft: 20 }}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {regla.es_base
            ? <Badge tone="info" icon={Anchor}>Regla base</Badge>
            : <Badge tone="progress">{regla.puesto ? regla.puesto.nombre : 'Puesto'}</Badge>}
          <span className={`truncate text-sm font-medium ${regla.activa ? 'text-v25-ink-900' : 'text-v25-ink-400'}`}>{regla.titulo}</span>
          {!regla.activa && <Badge tone="expired">Desactivada</Badge>}
        </div>
        <p className="mt-1 text-xs text-v25-ink-500">{regla.areas_count} área(s) · {regla.personas_count} persona(s) directa(s)</p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Tooltip content="Editar"><Button variant="ghost" size="icon" onClick={onEditar}><Pencil /></Button></Tooltip>
        {regla.activa ? (
          <Tooltip content="Desactivar"><Button variant="ghost" size="icon" onClick={onDesactivar}><Ban /></Button></Tooltip>
        ) : (
          <Tooltip content="Reactivar">
            <Button variant="ghost" size="icon" disabled={accion} onClick={onReactivar}>
              {accion ? <Loader2 className="animate-spin" /> : <RotateCcw />}
            </Button>
          </Tooltip>
        )}
        <span className="mx-0.5 h-5 w-px shrink-0 bg-v25-line" aria-hidden="true" />
        <Tooltip content="Eliminar permanentemente">
          <Button variant="ghost" size="icon" onClick={onEliminar} className="text-red-600 hover:bg-red-50 hover:text-red-700"><Trash2 /></Button>
        </Tooltip>
      </div>
    </div>
  );
}

export default function Avisos() {
  const [reglas, setReglas] = useState([]);
  const [negocios, setNegocios] = useState([]);
  const [puestos, setPuestos] = useState([]);
  const [areas, setAreas] = useState([]);
  const [sinBaseIds, setSinBaseIds] = useState(new Set());
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);
  const [accionId, setAccionId] = useState(null);
  const [editor, setEditor] = useState(null);        // { regla|null }
  const [aDesactivar, setADesactivar] = useState(null);
  const [aEliminar, setAEliminar] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true); setError(false);
    try {
      const [rg, ns, ps, ar, diag] = await Promise.all([
        listReglasAviso(), listNegocios(), listPuestos(), listAreas(), diagnosticoReglas(),
      ]);
      setReglas(rg);
      setNegocios(ns?.negocios || []);
      setPuestos(ps);
      setAreas(ar);
      setSinBaseIds(new Set((diag?.negocios_sin_base || []).map((n) => n.id)));
    } catch (e) {
      console.error('[incorp][avisos] cargar', e);
      setError(true);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const areasActivas = useMemo(() => areas.filter((a) => a.activa), [areas]);

  async function reactivar(r) {
    setAccionId(r.id);
    try {
      const d = await getReglaAviso(r.id);
      await actualizarReglaAviso(r.id, {
        negocio_id: d.negocio_id, puesto_id: d.puesto_id, titulo: d.titulo, activa: true,
        areas: d.areas.map((a) => a.id), personas: d.personas.map((p) => p.id),
      });
      toast.success('Regla reactivada.');
      await cargar();
    } catch (e) {
      console.error('[incorp][avisos] reactivar', e);
      toast.error(cuerpoError(e).message || 'No se pudo reactivar la regla.');
    } finally {
      setAccionId(null);
    }
  }

  // Agrupación por negocio (TODOS los negocios, también los sin reglas).
  const secciones = useMemo(() => negocios.map((n) => {
    const reglasNeg = reglas.filter((r) => r.negocio_id === n.id);
    const base = reglasNeg.find((r) => r.es_base) || null;
    const especificas = reglasNeg.filter((r) => !r.es_base).sort((a, b) => (a.puesto?.nombre || '').localeCompare(b.puesto?.nombre || ''));
    return { negocio: n, base, especificas, sinBase: sinBaseIds.has(n.id) };
  }), [negocios, reglas, sinBaseIds]);

  const rowProps = (r) => ({
    accion: accionId === r.id,
    onEditar: () => setEditor({ regla: r }),
    onDesactivar: () => setADesactivar(r),
    onReactivar: () => reactivar(r),
    onEliminar: () => setAEliminar(r),
  });

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        title="Avisos"
        subtitle="Reglas de aviso: a quién se notifica cuando entra alguien. La regla base cubre todo el negocio; las de puesto se suman."
        action={<Button onClick={() => setEditor({ regla: null })}><Plus />Nueva regla</Button>}
      />

      <IncorporacionesTabs active="avisos" />

      {cargando ? (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-v25-ink-500">
          <Loader2 className="h-5 w-5 animate-spin" /> Cargando reglas…
        </div>
      ) : error ? (
        <div className="rounded-v25-lg border border-v25-line bg-white py-16 text-center">
          <AlertCircle className="mx-auto mb-2 h-6 w-6 text-red-500" />
          <p className="mb-4 text-sm text-red-600">No se pudieron cargar las reglas de aviso.</p>
          <Button variant="outline" onClick={cargar}><RefreshCw />Reintentar</Button>
        </div>
      ) : (
        <>
          <PanelComprobacion negocios={negocios} puestos={puestos} />

          {negocios.length === 0 ? (
            <EmptyState variant="minimal" title="No hay negocios." />
          ) : (
            <div className="space-y-4">
              {secciones.map(({ negocio, base, especificas, sinBase }) => (
                <section key={negocio.id} className="rounded-v25-lg border border-v25-line bg-white p-4">
                  <div className="mb-3 flex items-center gap-2">
                    <Bell className="h-4 w-4 text-v25-ink-400" />
                    <h3 className="text-sm font-semibold text-v25-ink-900">{negocio.nombre}</h3>
                  </div>

                  {sinBase && (
                    <div className="mb-3 flex items-start gap-2 rounded-v25-md border border-v25-expired-bd bg-v25-expired-bg p-3 text-xs text-v25-expired-fg">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      <span><strong>Sin regla base activa.</strong> Ninguna alta de este negocio avisará a nadie hasta que se cree una.</span>
                    </div>
                  )}

                  <div className="space-y-2">
                    {base && <ReglaRow regla={base} {...rowProps(base)} />}
                    {especificas.map((r) => <ReglaRow key={r.id} regla={r} {...rowProps(r)} />)}
                    {!base && especificas.length === 0 && (
                      <p className="text-xs text-v25-ink-400">Sin reglas.</p>
                    )}
                  </div>
                </section>
              ))}
            </div>
          )}
        </>
      )}

      {editor && (
        <ReglaModal
          regla={editor.regla}
          negocios={negocios}
          puestos={puestos}
          areasActivas={areasActivas}
          onClose={() => setEditor(null)}
          onGuardado={() => { setEditor(null); cargar(); }}
        />
      )}

      {aDesactivar && (
        <DesactivarReglaModal
          regla={aDesactivar}
          onClose={() => setADesactivar(null)}
          onDesactivado={() => { setADesactivar(null); cargar(); }}
        />
      )}

      {aEliminar && (
        <EliminarModal
          titulo="Eliminar regla"
          subtitulo={aEliminar.negocio ? `${aEliminar.negocio.nombre} · ${aEliminar.es_base ? 'Regla base' : (aEliminar.puesto ? aEliminar.puesto.nombre : 'Puesto')}` : aEliminar.titulo}
          exitoMsg="Regla eliminada permanentemente."
          eliminar={() => eliminarReglaAvisoPermanente(aEliminar.id)}
          onClose={() => setAEliminar(null)}
          onEliminado={() => { setAEliminar(null); cargar(); }}
        >
          Se eliminará la regla <strong>«{aEliminar.titulo}»</strong> de forma permanente, con sus{' '}
          <strong>{aEliminar.areas_count} área(s)</strong> y <strong>{aEliminar.personas_count} persona(s) directa(s)</strong> asociadas
          (los enlaces de la regla, no las áreas ni las personas en sí). Esta acción no se puede deshacer.
        </EliminarModal>
      )}
    </div>
  );
}

// ─────────────────────────── Modal editor ───────────────────────────
function ReglaModal({ regla, negocios, puestos, areasActivas, onClose, onGuardado }) {
  const esEdicion = !!(regla && regla.id);
  const [cargandoDetalle, setCargandoDetalle] = useState(esEdicion);
  const [negocioId, setNegocioId] = useState('');
  const [alcance, setAlcance] = useState('base'); // 'base' | 'puesto'
  const [puestoId, setPuestoId] = useState('');
  const [titulo, setTitulo] = useState('');
  const [areasSel, setAreasSel] = useState(new Set());
  const [personas, setPersonas] = useState([]); // [{id,nombre}]
  const [guardando, setGuardando] = useState(false);
  const [errores, setErrores] = useState({});
  const [reactivable, setReactivable] = useState(null);
  // Efecto combinado (guardado) para negocio+alcance
  const [efecto, setEfecto] = useState(null);
  const [efectoCargando, setEfectoCargando] = useState(false);
  const [efectoError, setEfectoError] = useState(false);
  const cargadoRef = useRef(false);

  useEffect(() => {
    if (!esEdicion) { cargadoRef.current = true; return undefined; }
    let cancel = false;
    getReglaAviso(regla.id)
      .then((d) => {
        if (cancel) return;
        setNegocioId(String(d.negocio_id));
        setAlcance(d.puesto_id ? 'puesto' : 'base');
        setPuestoId(d.puesto_id ? String(d.puesto_id) : '');
        setTitulo(d.titulo || '');
        setAreasSel(new Set(d.areas.map((a) => a.id)));
        setPersonas(d.personas.map((p) => ({ id: p.id, nombre: p.nombre })));
      })
      .catch(() => { if (!cancel) toast.error('No se pudo cargar la regla.'); })
      .finally(() => { if (!cancel) { setCargandoDetalle(false); cargadoRef.current = true; } });
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esEdicion, regla]);

  const puestosDelNegocio = useMemo(
    /* Solo puestos de INTERNO: un aviso cuelga de un negocio (`incorp_reglas_aviso.negocio_id`) y
     * un puesto de colaborador no tiene negocio. Antes esto salia bien por accidente -su
     * `negocio_id` a NULL no casaba con ningun id-; ahora se dice, porque una regla que se cumple
     * sola deja de cumplirse el dia que alguien toca lo de al lado. */
    () => activosDelAmbito(puestos, { tipo: INTERNO, negocioId }),
    [puestos, negocioId],
  );

  // Efecto combinado: /resolver(negocio, puesto del alcance). Refleja lo GUARDADO.
  const puestoEfectivo = alcance === 'puesto' ? puestoId : '';
  useEffect(() => {
    if (!cargadoRef.current || !negocioId) { setEfecto(null); return undefined; }
    let cancel = false;
    setEfectoCargando(true); setEfectoError(false);
    resolverDestinatarios(Number(negocioId), puestoEfectivo ? Number(puestoEfectivo) : null)
      .then((d) => { if (!cancel) setEfecto(d); })
      .catch(() => { if (!cancel) setEfectoError(true); })
      .finally(() => { if (!cancel) setEfectoCargando(false); });
    return () => { cancel = true; };
  }, [negocioId, puestoEfectivo]);

  function cambiarNegocio(v) { setNegocioId(v); setPuestoId(''); }
  function cambiarAlcance(v) { setAlcance(v); if (v === 'base') setPuestoId(''); }
  const toggleArea = (id) => setAreasSel((prev) => { const s = new Set(prev); if (s.has(id)) s.delete(id); else s.add(id); return s; });
  const anadirPersona = (p) => setPersonas((prev) => (prev.some((x) => x.id === p.id) ? prev : [...prev, { id: p.id, nombre: p.nombre }]));
  const quitarPersona = (id) => setPersonas((prev) => prev.filter((p) => p.id !== id));

  function validar() {
    const err = {};
    if (!negocioId) err.negocio = 'Selecciona un negocio.';
    if (alcance === 'puesto' && !puestoId) err.puesto = 'Selecciona un puesto.';
    if (!titulo.trim()) err.titulo = 'El título es obligatorio.';
    else if (titulo.trim().length > 120) err.titulo = 'Máximo 120 caracteres.';
    setErrores(err);
    if (Object.keys(err).length) return null;
    return {
      negocio_id: Number(negocioId),
      puesto_id: alcance === 'puesto' ? Number(puestoId) : null,
      titulo: titulo.trim(),
      areas: [...areasSel],
      personas: personas.map((p) => p.id),
    };
  }

  async function guardar() {
    const payload = validar();
    if (!payload) return;
    setGuardando(true); setReactivable(null);
    try {
      if (esEdicion) { await actualizarReglaAviso(regla.id, payload); toast.success('Regla actualizada.'); }
      else { await crearReglaAviso(payload); toast.success('Regla creada.'); }
      onGuardado();
    } catch (e) {
      const body = cuerpoError(e);
      if (body.error === 'regla_duplicada_inactiva' && body.id) setReactivable({ id: body.id });
      else if (body.error === 'regla_duplicada') toast.error(body.message || 'Ya existe esa regla.');
      else { console.error('[incorp][avisos] guardar', e); toast.error(body.message || 'No se pudo guardar la regla.'); }
    } finally {
      setGuardando(false);
    }
  }

  async function reactivarDuplicada() {
    if (!reactivable) return;
    const payload = validar();
    if (!payload) return;
    setGuardando(true);
    try {
      await actualizarReglaAviso(reactivable.id, { ...payload, activa: true });
      toast.success('Regla reactivada con estos valores.');
      onGuardado();
    } catch (e) {
      console.error('[incorp][avisos] reactivarDuplicada', e);
      toast.error(cuerpoError(e).message || 'No se pudo reactivar la regla.');
    } finally {
      setGuardando(false);
    }
  }

  const footer = reactivable ? (
    <>
      <Button variant="outline" onClick={onClose} disabled={guardando}>Cancelar</Button>
      <Button onClick={reactivarDuplicada} disabled={guardando}>
        {guardando ? <Loader2 className="animate-spin" /> : <RotateCcw />}Reactivar la regla existente
      </Button>
    </>
  ) : (
    <>
      <Button variant="outline" onClick={onClose} disabled={guardando}>Cancelar</Button>
      <Button onClick={guardar} disabled={guardando || cargandoDetalle}>
        {guardando ? <Loader2 className="animate-spin" /> : null}{esEdicion ? 'Guardar cambios' : 'Crear regla'}
      </Button>
    </>
  );

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={esEdicion ? 'Editar regla de aviso' : 'Nueva regla de aviso'}
      subtitle={esEdicion ? regla.titulo : 'Base del negocio o específica de un puesto.'}
      footer={footer}
    >
      {cargandoDetalle ? (
        <div className="flex items-center justify-center gap-2 py-12 text-sm text-v25-ink-500"><Loader2 className="h-5 w-5 animate-spin" /> Cargando regla…</div>
      ) : (
        <div className="space-y-5">
          {/* Identidad */}
          <section className="space-y-3">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-v25-ink-500">Identidad</h4>
            <Select label="Negocio *" value={negocioId} onChange={(e) => cambiarNegocio(e.target.value)} error={errores.negocio}>
              <option value="">Selecciona un negocio…</option>
              {negocios.map((n) => <option key={n.id} value={n.id}>{n.nombre}</option>)}
            </Select>
            <div>
              <label className="mb-1 block text-xs font-medium text-v25-ink-700">Alcance *</label>
              <ToggleGroup value={alcance} onChange={cambiarAlcance}>
                <ToggleItem value="base" label="Regla base (todo el negocio)" icon={Anchor} />
                <ToggleItem value="puesto" label="Puesto específico" />
              </ToggleGroup>
              <p className="mt-1 text-[11px] text-v25-ink-400">
                La base se aplica a toda alta del negocio. Las de puesto <strong>se suman</strong> a la base.
              </p>
            </div>
            {alcance === 'puesto' && (
              <Select label="Puesto *" value={puestoId} onChange={(e) => setPuestoId(e.target.value)} error={errores.puesto}
                disabled={!negocioId} hint={!negocioId ? 'Elige antes un negocio.' : (puestosDelNegocio.length === 0 ? 'Este negocio no tiene puestos activos.' : undefined)}>
                <option value="">Selecciona un puesto…</option>
                {puestosDelNegocio.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
              </Select>
            )}
            <Input label="Título *" value={titulo} onChange={(e) => setTitulo(e.target.value)} error={errores.titulo} maxLength={120} />
          </section>

          {/* Áreas */}
          <section className="space-y-2 border-t border-v25-line pt-4">
            <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-v25-ink-500"><Users className="h-3.5 w-3.5" /> Áreas a avisar</h4>
            {areasActivas.length === 0 ? (
              <p className="text-xs text-v25-ink-400">No hay áreas activas.</p>
            ) : (
              <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                {areasActivas.map((a) => (
                  <label key={a.id} className="flex cursor-pointer items-center gap-2 rounded-v25-md border border-v25-line px-2.5 py-1.5 hover:bg-v25-surface-sunken">
                    <input type="checkbox" checked={areasSel.has(a.id)} onChange={() => toggleArea(a.id)} className="h-4 w-4" />
                    <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: a.color || '#94a3b8' }} />
                    <span className="truncate text-sm text-v25-ink-800">{a.nombre}</span>
                    {a.miembros_count === 0 && <Badge tone="expired" className="ml-auto shrink-0">sin miembros</Badge>}
                  </label>
                ))}
              </div>
            )}
          </section>

          {/* Personas directas */}
          <section className="space-y-2 border-t border-v25-line pt-4">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-v25-ink-500">Personas directas (internas)</h4>
            <PersonaPickerMiembro onAdd={anadirPersona} excluirIds={personas.map((p) => p.id)} />
            {personas.length === 0 ? (
              <p className="text-xs text-v25-ink-400">Sin personas directas. Los avisos irán solo a los miembros de las áreas elegidas.</p>
            ) : (
              <ul className="divide-y divide-v25-line rounded-v25-md border border-v25-line">
                {personas.map((p) => (
                  <li key={p.id} className="flex items-center justify-between gap-2 px-2 py-1.5">
                    <div className="flex min-w-0 items-center gap-2"><Avatar name={p.nombre} size="sm" /><span className="truncate text-sm text-v25-ink-800">{p.nombre}</span></div>
                    <button type="button" onClick={() => quitarPersona(p.id)} className="shrink-0 text-v25-ink-400 hover:text-red-600" aria-label={`Quitar a ${p.nombre}`}><X className="h-4 w-4" /></button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Efecto combinado (guardado) */}
          <section className="space-y-2 border-t border-v25-line pt-4">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-v25-ink-500">Efecto combinado</h4>
            <p className="text-[11px] text-v25-ink-400">
              Quién se entera hoy con {alcance === 'puesto' ? 'esta regla + la base' : 'la base'} (refleja lo <strong>guardado</strong>; se recalcula al guardar). Cada procedencia indica si viene de la <strong>Base</strong> o del <strong>Puesto</strong>.
            </p>
            <DestinatariosResueltos data={efecto} cargando={efectoCargando} error={efectoError} />
          </section>

          {reactivable && (
            <div className="flex items-start gap-2 rounded-v25-md border border-v25-received-bd bg-v25-received-bg p-3 text-xs text-v25-received-fg">
              <RotateCcw className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Ya existe una regla <strong>desactivada</strong> para esa combinación. En vez de crear otra, puedes reactivarla con estos valores.</span>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

// ─────────────────────────── Modal danger (desactivar) ───────────────────────────
function DesactivarReglaModal({ regla, onClose, onDesactivado }) {
  const [procesando, setProcesando] = useState(false);
  async function confirmar() {
    setProcesando(true);
    try {
      await desactivarReglaAviso(regla.id);
      toast.success('Regla desactivada.');
      onDesactivado();
    } catch (e) {
      console.error('[incorp][avisos] desactivar', e);
      toast.error(cuerpoError(e).message || 'No se pudo desactivar la regla.');
    } finally {
      setProcesando(false);
    }
  }
  return (
    <Modal open onClose={onClose} size="sm" tone="danger" title="Desactivar regla" subtitle={regla.titulo}
      footer={(
        <>
          <Button variant="outline" onClick={onClose} disabled={procesando}>Cancelar</Button>
          <Button variant="destructive" onClick={confirmar} disabled={procesando}>
            {procesando ? <Loader2 className="animate-spin" /> : <Ban />}Desactivar
          </Button>
        </>
      )}>
      <p className="text-sm text-v25-ink-700">
        La regla dejará de aplicarse a las altas. {regla.es_base && <strong>Es la regla base: sin ella, ninguna alta del negocio avisará a nadie.</strong>} Podrás reactivarla después. ¿Seguro?
      </p>
    </Modal>
  );
}
